import { Package } from "lucide-react";
import type { FeedItem } from "@/stores/devAgentStore";
import { PanelSection } from "./PanelSection";
import type { ArtifactFileRow } from "./derivePanelData";

type WorkspaceBadge = "R" | "C" | "M" | "D";

function resolveWorkspaceBadge(params: {
  file: ArtifactFileRow;
  artifactBadges: Record<string, "R" | "C" | "M">;
  feedItems: FeedItem[];
}): WorkspaceBadge | undefined {
  const { file, artifactBadges, feedItems } = params;
  const explicitBadge = artifactBadges[file.id] || artifactBadges[file.name];
  if (explicitBadge) return explicitBadge;

  const feedItem = feedItems.find(
    (item) =>
      item.type === "tool_call" &&
      item.data.status === "done" &&
      (item.data.args?.name === file.name || item.data.metadataId === file.id),
  );
  const toolName = feedItem?.data?.name || "";
  const hasCodeChange = feedItems.some(
    (item) => item.type === "code_change" && item.data.fileName === file.name,
  );
  const isDependencyArtifact =
    toolName.includes("graph") || toolName.includes("impact") || toolName.includes("dependent");

  if (hasCodeChange) return "M";
  if (isDependencyArtifact) return "D";
  return undefined;
}

function workspaceBadgePresentation(badge: WorkspaceBadge | undefined) {
  if (badge === "R") return { className: "bg-green-500/20 text-green-400", title: "Read from org" };
  if (badge === "C") return { className: "bg-blue-500/20 text-blue-400", title: "Created / generated" };
  if (badge === "M") return { className: "bg-yellow-500/20 text-yellow-400", title: "Modified by user" };
  if (badge === "D") return { className: "bg-purple-500/20 text-purple-400", title: "Dependency" };
  return { className: "bg-muted text-muted-foreground", title: "" };
}

export function WorkspacePanel({
  artifactFiles,
  artifactBadges,
  feedItems,
  isBusy,
  onClear,
  onMoveUp,
  onMoveDown,
  onOpenFileInEditor,
}: {
  artifactFiles: ArtifactFileRow[];
  artifactBadges: Record<string, "R" | "C" | "M">;
  feedItems: FeedItem[];
  isBusy?: boolean;
  onClear?: () => void;
  onMoveUp?: () => void;
  onMoveDown?: () => void;
  onOpenFileInEditor: (file: { id: string; name: string; metadataType?: string }) => void;
}) {
  return (
    <PanelSection
      disabled={isBusy}
      title="Workspace"
      icon={<Package className="w-3.5 h-3.5" />}
      count={artifactFiles.length}
      onClear={onClear}
      onMoveUp={onMoveUp}
      onMoveDown={onMoveDown}
    >
      {artifactFiles.length > 0 ? (
        <div className="space-y-1">
          {artifactFiles.map((file) => {
            const badge = resolveWorkspaceBadge({ file, artifactBadges, feedItems });
            const presentation = workspaceBadgePresentation(badge);

            return (
              <button
                key={file.id}
                onClick={() =>
                  onOpenFileInEditor({
                    id: file.id,
                    name: file.name,
                    metadataType: file.metadataType || "ApexClass",
                  })
                }
                disabled={isBusy}
                className="flex items-start gap-2 w-full py-1.5 px-1 rounded hover:bg-muted/50 transition-colors text-left group disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:bg-transparent"
              >
                {badge ? (
                  <span
                    className={`text-[10px] font-bold px-1 rounded shrink-0 mt-0.5 ${presentation.className}`}
                    title={presentation.title}
                  >
                    {badge}
                  </span>
                ) : (
                  <span className="w-4 h-4 rounded flex items-center justify-center text-[9px] font-bold shrink-0 bg-muted text-muted-foreground mt-0.5" />
                )}
                <div className="min-w-0 flex-1">
                  <div className="truncate text-foreground">{file.name}</div>
                  {(file.summary || file.metadataType) && (
                    <div className="truncate text-[10px] text-muted-foreground/75">
                      {file.summary || file.metadataType}
                    </div>
                  )}
                </div>
                <span className="text-[9px] text-muted-foreground/75 opacity-0 group-hover:opacity-100 transition-opacity shrink-0">
                  {file.operation || file.metadataType}
                </span>
              </button>
            );
          })}
        </div>
      ) : (
        <div className="text-muted-foreground text-center py-2">
          No files loaded yet
        </div>
      )}
    </PanelSection>
  );
}
